import { HttpError, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLogButton } from "components/historyLogButton";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { IAdminAccountDetail, IOrgEventIdsItem, IOrgIdsItem } from "interfaces";
import { Link } from "react-router-dom";
import Button from "components/button";
import { PencilIcon } from "@heroicons/react/outline";
import { arrayCompare, formatPhoneNumber, isHaveActionPermission } from "utills/helpers";
import { ALL_OPTION } from "utills/constants";

export const AdminAccountDetail: React.FC = () => {
  const parse = useParse();
  const { params } = parse();
  const { data, isFetching, isPreviousData, isLoading } =
    useOne<IAdminAccountDetail>({
      resource: "admin-account/account",
      id: params?.id,
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: !!params?.id,
      } as any,
    });

  const accountDetail = data?.data ?? null;

  const { data: organizationIdsData } = useOne<IOrgIdsItem[], HttpError>({
    resource: "organization",
    id: "ids",
    meta: {
      resource: "data",
    },

    queryOptions: {
      enabled: !!accountDetail,
    } as any,
  });
  const { data: eventIdsData } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "content/dropdown",
    id: "events",
    meta: {
      resource: "data",
      filters: [
        {
          field: "uncheck",
          operator: "eq",
          value: "true",
        },
      ],
    },
    queryOptions: {
      enabled: !!accountDetail,
    } as any,
  });

  const organizationIds = (
    (organizationIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []
  ).concat(
    (organizationIdsData?.data ?? []).map((org: IOrgIdsItem) => ({
      value: org.id,
      label: org.name_en,
    }))
  );

  const organizationEventIds = (
    (eventIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []
  ).concat(
    (eventIdsData?.data ?? []).map((event: IOrgEventIdsItem) => ({
      value: event.id,
      label: event.name_en,
    }))
  );

  const isAllEvent = () => {
    const organizationMapping = accountDetail?.organization_mapping;
    if (
      typeof organizationMapping !== "object" ||
      organizationMapping === null
    ) {
      return false;
    }

    const keys = Object.keys(organizationMapping);
    return keys.length === 0;
  };

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
        <Breadcrumbs 
          customPageLabel="Admin Account Detail"
          additionalLabel={(
            <>
              {accountDetail ? (
                <div className="flex gap-x-2.5">
                  <div>
                    <HistoryLogButton
                      resource={`admin-account/account/${accountDetail?.id}/history`}
                      className="sm:h-auto"
                    />
                  </div>
                  {isHaveActionPermission("account", "admin", "edit") ? (
                    <Link
                      to={`/admin-accounts/accounts/${accountDetail?.id}/edit`}
                    >
                      <Button className="h-10 w-10 sm:h-auto sm:w-auto">
                        <span className="sr-only">Edit</span>
                        <PencilIcon className="w-4 h-4 text-white" />{" "}
                        <span className="hidden sm:block sm:ml-2">Edit</span>
                      </Button>
                    </Link>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        />
      <div className="box-container p-0">
        {accountDetail ? (
          <div className="divide-y divide-gray-100">
            <div className="box-container bg-transparent">
              <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-8 lg:gap-x-6">
                <div className="sm:col-span-2">
                  <Label required id="is_active" name="Status" />
                  <SwitchButton
                    checked={accountDetail.id ? true : false}
                    onChange={() => {}}
                    disabled
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label required id="email" name="Email" />
                  <p>{accountDetail.email}</p>
                </div>
                <div className="sm:col-span-2">
                  <Label id="first_name" name="First name" />
                  <p>{accountDetail.first_name}</p>
                </div>
                <div className="sm:col-span-2">
                  <Label id="last_name" name="Last name" />
                  <p>{accountDetail.last_name}</p>
                </div>
                <div className="sm:col-span-2">
                  <Label id="role_name" name="Role name" />
                  <p>{accountDetail.role_name}</p>
                </div>
                <div className="sm:col-span-6">
                  <Label id="phone" name="Contact Phone" />
                  <p>
                    {formatPhoneNumber(
                      accountDetail.phone,
                      accountDetail.phone_area_code
                    )}
                  </p>
                </div>
                <div className="sm:col-span-4">
                  <Label required id="org_id" name="Org name" />
                  {(accountDetail.organization_ids ?? []).map((id) => (
                    <p key={id}>
                      {
                        (organizationIds ?? []).find(
                          (orgId) => orgId.value === id
                        )?.label
                      }
                    </p>
                  ))}
                </div>
                <div className="sm:col-span-4">
                  <Label required id="event_id" name="Event" />
                  {isAllEvent()
                    ? "All Events"
                    : (
                      <div className="flex flex-col gap-y-2">
                        {Object.keys(accountDetail.organization_mapping ?? {}).map(
                          (key: string) => (
                            <div>
                              <p className="font-medium">
                                {(organizationIds ?? []).find(
                                  (orgId) => orgId.value === key
                                )?.label}
                              </p>
                              {accountDetail.organization_mapping[key].map(
                                (id: string) => (
                                  <p key={id}>
                                    {
                                      (organizationEventIds ?? []).find(
                                        (eventId) => eventId.value === id
                                      )?.label
                                    }
                                  </p>
                                )
                              )}
                            </div>
                          )
                        )}
                        {((accountDetail.organization_ids ?? []).length > Object.keys(accountDetail.organization_mapping ?? {}).length) || (arrayCompare((accountDetail.organization_ids ?? []), ["all"])) ? (
                          <div>
                            <p className="font-medium">
                              Other Orgs
                            </p>
                            <p>All Events</p>
                          </div>
                        ) : <></>}
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {isFetching && isPreviousData ? <FullScreenLoading /> : <></>}
    </div>
  );
};
